import axios from 'axios';
import jwt_decode from 'jwt-decode';
import { getAccessToken } from '../../../auth/oktaConfig';
import type { IRecipient } from '../../../common/recipients/types';
import { STATUSES } from '../../../components/envelopeManagement/utils';
import type { SmartEmailConfig } from '../../../components/senderBuild/EditEmailModal';
import type { IFile } from '../../../dtm/types';
import {
  type ICreateEmailBody,
  type IDocumentBody,
  type IEnvelopeHistoryBody,
  type ISignerDetails,
} from '../../../store/envelopeHistory/types';
import {
  type IEnvelopeMetadata,
  type IGetEnvelopesResponse,
  type ITokenBody,
} from '../../../store/envelopeManagement/types';
import { PermissionType } from '../../../store/pageFrame/types';
import type { AutoBlocksRes, IBlock, IEnvelope, IGroup } from '../../../store/senderBuild/types';
import { API_URL } from '../../environment-config';
import type { IResponse } from '../../types';
import { buildStreetAddress, getFileType } from '../../utils';
import { accountApi } from '../account';
import { headers } from '../common/helper';
import makePagedGraphQLRequest, { type IExtraArgs } from '../common/pagedGraphQLRequest';
import { updateProgressPercentageForDocument } from '../common/progressPercentage';
import Cache from './cache';

const envelopeStatusCache = new Cache(4000, Date);

export interface IEnvelopeDownloadResponse<_T> {
  data: any;
  status: number;
}

export interface EmailDefaults {
  subject: string;
  body: string;
}

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const getEnvelopesForUser = async (
  searchSubject: string,
  status: string,
  orderBy: string,
  orderByDesc: boolean
): Promise<IGetEnvelopesResponse> => {
  if (window.location.search.includes('?simulateEmpty')) {
    return {
      envelopes: [],
      totalEnvelopeCount: 0,
      endReached: true,
    };
  }
  const extraArgs: IExtraArgs = {
    orderBy,
    orderByDesc,
  };
  if (searchSubject) {
    extraArgs.searchSubject = `"${searchSubject}"`;
  }
  if (status) {
    // @ts-expect-error -- This code was written prior to enforcing lint rules
    const statusValues = STATUSES[status].statuses;
    if (statusValues?.length) {
      extraArgs.statuses = `[${statusValues.join(',')}]`;
    }
  }
  const envelopeDefinition = `{
    id
    name
    subject
    status
    accountId
    recipientCount
    signerCount
    completedSignerCount
    updatedDate
    sentDate
    completedDate
    createdDate
    senderCanSign
    config {
      senderUi {
        envelopePathPrefix
      }
    }
  }`;
  const accessToken = await getAccessToken();
  const res = await makePagedGraphQLRequest<IEnvelopeMetadata>(
    accessToken,
    `${API_URL}/envelopesgraphql`,
    'envelopeSearch',
    envelopeDefinition,
    20,
    extraArgs
  );

  return {
    envelopes: res.nodes.map((e) => ({ ...e, ...envelopeStatusCache.getSet(e.id, e) })),
    totalEnvelopeCount: res.totalCount,
    endReached: res.endReached,
  };
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const getEnvelopeCounts = async () => {
  const envelopeDefinition = `{
    id
  }`;
  const accessToken = await getAccessToken();
  const totalEnvelopesResult = await makePagedGraphQLRequest<IEnvelopeMetadata>(
    accessToken,
    `${API_URL}/envelopesgraphql`,
    'envelopeSearch',
    envelopeDefinition,
    1,
    {}
  );

  const totalSentEnvelopesResult = await makePagedGraphQLRequest<IEnvelopeMetadata>(
    accessToken,
    `${API_URL}/envelopesgraphql`,
    'envelopeSearch',
    envelopeDefinition,
    1,
    { statuses: '[Completed,Sent,InProgress,Canceled,Correcting]' }
  );

  const totalCompletedEnvelopesResult = await makePagedGraphQLRequest<IEnvelopeMetadata>(
    accessToken,
    `${API_URL}/envelopesgraphql`,
    'envelopeSearch',
    envelopeDefinition,
    1,
    { statuses: '[Completed]' }
  );

  return {
    envelopesCreated: totalEnvelopesResult.totalCount,
    envelopesSent: totalSentEnvelopesResult.totalCount,
    envelopesCompleted: totalCompletedEnvelopesResult.totalCount,
  };
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const getEnvelopeById = async (envelopeId: string): Promise<IResponse<IEnvelope>> => {
  const accessToken = await getAccessToken();
  const { data } = await axios.get(`${API_URL}/envelopes/${envelopeId}`, headers(accessToken));
  return data;
};

export async function getEmailDefaults(emailId: string): Promise<EmailDefaults> {
  const accessToken = await getAccessToken();
  const { data } = await axios.get(`${API_URL}/envelopes/emailDefaults/${emailId}`, headers(accessToken));
  return data;
}

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const graphQlRequest = async (query: string) => {
  const accessToken = await getAccessToken();
  return axios.post(`${API_URL}/envelopesGraphql`, { query }, headers(accessToken));
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const getEnvelopeDocuments = async (envelopeId: string): Promise<IDocumentBody[]> => {
  const { data } = await graphQlRequest(
    `{
    envelope(id: "${envelopeId}") {
      documents {
        id
        numberOfPages
        fileName
        presignedURL
        pages {
          height
          width
        }
      }
    }
  }`
  );
  return data.data.envelope.documents;
};

/** Recipients plus signerDetails.activeGroup, which is whose turn the service says it is. */
export async function getEnvelopeRecipientsWithTurn(
  envelopeId: string
): Promise<{ recipients: IRecipient[]; signerDetails?: ISignerDetails }> {
  const { data } = await graphQlRequest(
    `{
    envelope(id: "${envelopeId}") {
      recipients { id, firstName, middleName, lastName, email, role, status, title },
      signerDetails {
        details {
          key
          value {
            signerId
            signingGroupId
            signingStatus
          }
        }
        activeGroup
      },
    },
  }`
  );
  return data.data.envelope;
}

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const getEnvelopeRecipients = async (envelopeId: string): Promise<IRecipient[]> => {
  const { data } = await graphQlRequest(
    `{
    envelope(id: "${envelopeId}") {
      recipients {
        id,
        firstName,
        middleName,
        lastName,
        email,
        role,
        status,
        title,
      },
    },
  }`
  );
  return data.data.envelope.recipients;
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const getEnvelopeHistory = async (envelopeId: string): Promise<IEnvelopeHistoryBody> => {
  const { data } = await graphQlRequest(
    `{
    envelope(id: "${envelopeId}") {
      id,
      status,
      senderId,
      senderFirstName,
      senderMiddleName,
      senderLastName,
      senderEmail,
      sentDate,
      completedDate,
      recipients {
        id,
        role,
        firstName,
        middleName,
        lastName,
        email,
        status,
        title,
        isActive,
        isPlaceholder
      },
      documents {
        id,
        numberOfPages,
        fileName,
        presignedURL,
      },
      signerDetails {
        details {
          key
          value {
            signerId
            signingGroupId
            signingStatus
          }
        }
        activeGroup,
      },
      signingGroups {
        id,
        name,
        order,
      },
    },
  }`
  );
  return data;
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const sendEmail = async (envelopeId: string, createEmailConfig: ICreateEmailBody): Promise<void> => {
  const accessToken = await getAccessToken();
  const { status } = await axios.post(
    `${API_URL}/envelopes/${envelopeId}/emails`,
    createEmailConfig,
    headers(accessToken)
  );
  if (status !== 201) {
    throw new Error('An error has occured in the call to sendEmail');
  }
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const downloadEnvelope = async (envelopeId: string): Promise<IEnvelopeDownloadResponse<null>> => {
  const accessToken = await getAccessToken();
  const response = await axios.get(`${API_URL}/envelopes/${envelopeId}/pdf?api-version=2.0`, {
    responseType: 'blob',
    ...headers(accessToken),
  });
  const { data, status } = response;
  return {
    data,
    status,
  };
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const getPdfBlobFromUrl = async (downloadUrl: string): Promise<ArrayBuffer> => {
  const accessToken = await getAccessToken();
  const { data } = await axios.get(downloadUrl, { responseType: 'arraybuffer', ...headers(accessToken) });
  return data;
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const updateEmailConfig = async (envelopeId: string, emailConfig: { subject: string; body: string }) => {
  const accessToken = await getAccessToken();
  return axios.patch(`${API_URL}/envelopes/${envelopeId}/emailConfig`, emailConfig, headers(accessToken));
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const getSmartEmailConfig = async (smartConfig: SmartEmailConfig): Promise<string> => {
  const accessToken = await getAccessToken();
  const res = await axios.put(
    `${API_URL}/envelopes/${smartConfig.envelopeId}/smartEmailConfig`,
    smartConfig,
    headers(accessToken)
  );
  return res.data.result.body;
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const reorderDocumentsInEnvelope = async (envelopeId: string, envelopeConfig: { documentOrder: string[] }) => {
  const accessToken = await getAccessToken();
  return axios.patch(`${API_URL}/envelopes/${envelopeId}?api-version=2.0`, envelopeConfig, headers(accessToken));
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const getAuditHistory = async (envelopeId: string) => {
  const accessToken = await getAccessToken();
  const { data } = await axios.get(`${API_URL}/envelopes/${envelopeId}/history`, headers(accessToken));
  return data;
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const uploadDocumentToEnvelope = async (envelopeId: string, documentId: string, file: File) => {
  const accessToken = await getAccessToken();
  const formData = new FormData();
  formData.append('fileName', file.name);
  formData.append('pdf', file);

  const { data, status } = await axios.post(
    `${API_URL}/envelopes/${envelopeId}/documents?api-version=2.0&enableAutoBlocks=true`,
    formData,
    {
      onUploadProgress: (e) => {
        updateProgressPercentageForDocument(e, documentId);
      },
      ...headers(accessToken),
    }
  );
  return { result: data, status };
};

export async function sendEnvelope(envelopeId: string) {
  let hasDs3Permission: boolean | undefined = undefined;
  try {
    // TODO: This call will FAIL for start-safe users because a sender token is being sent instead of a user token
    const memberships = await accountApi.getUsersMemberships();
    hasDs3Permission = memberships?.subscriptions
      ?.filter((s) => s.source !== 'Forms/DS3 Trial')
      .some((s) => s.permissions.includes(PermissionType.DS3));
  } catch {
    // `hasDs3Permission = undefined` is an escape hatch to avoid sending things to the DS3 spam filter
    hasDs3Permission = undefined;
  }

  const accessToken = await getAccessToken();
  const { status } = await axios.put(
    `${API_URL}/envelopes/${envelopeId}/status?api-version=2.0`,
    { status: 'Sent', flag: hasDs3Permission },
    headers(accessToken)
  );
  if (status === 201) {
    const currentDate = new Date().toISOString();
    envelopeStatusCache.set(envelopeId, {
      updatedDate: currentDate,
      sentDate: currentDate,
      id: envelopeId,
      status: 'Sent',
    });
  }

  return status;
}

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const deleteDocument = async (envelopeId: string, documentId: string) => {
  const accessToken = await getAccessToken();
  const { status } = await axios.delete(
    `${API_URL}/envelopes/${envelopeId}/documents/${documentId}?api-version=2.0`,
    headers(accessToken)
  );
  return {
    result: null,
    status,
  };
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const createEmptyEnvelope = async (impersonatedPrimeUserGuid?: string) => {
  const accessToken = await getAccessToken();
  const d = new Date();
  d.setFullYear(d.getFullYear() + 1);
  const expiration = d.toISOString().split('T')[0];
  const claims: ITokenBody = jwt_decode(accessToken);
  const body: { expiration: string; metadata: { primeUserId?: string } } = {
    expiration,
    metadata: {},
  };
  if (impersonatedPrimeUserGuid || claims.prime_user_guid) {
    body.metadata.primeUserId = impersonatedPrimeUserGuid || claims.prime_user_guid;
  }

  const { data, status } = await axios.post(`${API_URL}/envelopes?api-version=2.0`, body, headers(accessToken));
  return {
    result: data,
    status,
  };
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const renameEnvelope = async (payload: { envelopeId: string; updatedName: string }) => {
  const { envelopeId, updatedName } = payload;
  const accessToken = await getAccessToken();
  const { status } = await axios.put(
    `${API_URL}/envelopes/${envelopeId}/name?api-version=2.0`,
    { name: updatedName },
    headers(accessToken)
  );
  if (status !== 200) {
    throw new Error('Failed to rename envelope.');
  }
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const deleteEnvelope = async (envelopeId: string): Promise<void> => {
  const accessToken = await getAccessToken();
  const { status } = await axios.delete(`${API_URL}/envelopes/${envelopeId}`, headers(accessToken));
  if (status !== 204) {
    throw new Error('');
  }
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const cancelEnvelope = async (envelopeId: string, cancelBody: { canceledReason: string }): Promise<void> => {
  const accessToken = await getAccessToken();
  const { status } = await axios.patch(`${API_URL}/envelopes/${envelopeId}/cancel`, cancelBody, headers(accessToken));
  if (status !== 200) {
    throw new Error('An error has occured in the call to cancelEnvelope.');
  }
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const correctEnvelope = async (envelopeId: string): Promise<void> => {
  const accessToken = await getAccessToken();
  const correctingStatus = 'Correcting';
  const { status } = await axios.put(
    `${API_URL}/envelopes/${envelopeId}/status?api-version=2.0`,
    { status: correctingStatus },
    headers(accessToken)
  );
  if (status !== 201) {
    throw new Error('An error has occured in the call to change the envelope status.');
  }
  envelopeStatusCache.set(envelopeId, {
    updatedDate: new Date().toISOString(),
    id: envelopeId,
    status: correctingStatus,
  });
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
const cleanBlockForAdd = (block: IBlock) => {
  const strings = ['Signature', 'Initials', 'FullName', 'DateSigned', 'Strike', 'Rectangle'];
  if (strings.includes(block.blockType)) {
    const { value, ...blockWithoutValue } = block;
    return blockWithoutValue;
  }
  if (['Checkbox', 'ReadOnlyCheckbox'].includes(block.blockType)) {
    return { ...block, value: block.value === true };
  }
  return { ...block, value: block.value || '' };
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const addBlock = async (applicationId: string, documentId: string, block: IBlock) => {
  // TODO https://skyslope.atlassian.net/browse/DIGI-1378
  const accessToken = await getAccessToken();
  const blockWithValueCleanedUp = cleanBlockForAdd(block);
  const { data, status } = await axios.post(
    `${API_URL}/envelopes/${applicationId}/documents/${documentId}/blocks?api-version=2.0`,
    blockWithValueCleanedUp,
    headers(accessToken)
  );
  return {
    result: data,
    status,
  };
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const editBlock = async (applicationId: string, documentId: string, block: IBlock) => {
  const accessToken = await getAccessToken();
  const { blockId } = block;
  const { status } = await axios.put(
    `${API_URL}/envelopes/${applicationId}/documents/${documentId}/blocks/${blockId}?api-version=2.0`,
    block,
    headers(accessToken)
  );
  return status;
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const deleteBlock = async (envelopeId: string, documentId: string, blockId: string) => {
  const accessToken = await getAccessToken();
  const { status } = await axios
    .delete(
      `${API_URL}/envelopes/${envelopeId}/documents/${documentId}/blocks/${blockId}?api-version=2.0`,
      headers(accessToken)
    )
    .catch((e) => {
      return e;
    });
  if (status) {
    return status;
  }
  return new Error('404');
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const createNewGroup = async (envelopeId: string, group: IGroup) => {
  const accessToken = await getAccessToken();
  const postBody = {
    blockGroupType: 'CheckboxGroup',
    pageNumber: group.pageNumber,
    blockIds: group.blockIds,
    validation: group.validation,
  };
  const { data, status } = await axios.post(
    `${API_URL}/envelopes/${envelopeId}/documents/${group.documentId}/blockGroups?api-version=2.0`,
    postBody,
    headers(accessToken)
  );
  return {
    result: data.result.blockGroupId,
    status,
  };
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const editBlockGroup = async (envelopeId: string, group: IGroup) => {
  const accessToken = await getAccessToken();
  const postBody = {
    blockGroupType: 'CheckboxGroup',
    pageNumber: group.pageNumber,
    blockIds: group.blockIds,
    validation: group.validation,
  };
  const { status } = await axios.put(
    `${API_URL}/envelopes/${envelopeId}/documents/${group.documentId}/blockGroups/${group.id}?api-version=2.0`,
    postBody,
    headers(accessToken)
  );
  return {
    status,
  };
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const deleteBlockGroup = async (envelopeId: string, documentId: string, groupId: string, blockId?: string) => {
  const accessToken = await getAccessToken();
  const queryParam = blockId ? `blockId=${blockId}` : '';
  const { data, status } = await axios.delete(
    `${API_URL}/envelopes/${envelopeId}/documents/${documentId}/blockGroups/${groupId}?api-version=2.0&${queryParam}`,
    headers(accessToken)
  );
  return {
    result: data,
    status,
  };
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const createEmptyEnvelopeDTM = async (file: IFile): Promise<string> => {
  const accessToken = await getAccessToken();
  const d = new Date();
  d.setFullYear(d.getFullYear() + 1);
  const expiration = d.toISOString().split('T')[0];
  const fileStreetAddress = buildStreetAddress(file.property);

  const { data } = await axios.post(
    `${API_URL}/envelopes?api-version=2.0`,
    {
      expiration,
      config: {
        senderUi: {
          envelopePathPrefix: 'dtm/',
        },
      },
      metadata: {
        primeUserId: file.agent.id,
        listingGuid: file.objectType === 'listing' ? file.id : null,
        saleGuid: file.objectType === 'listing' ? null : file.id,
        fileStreetAddress: fileStreetAddress || undefined,
        fileCity: file.property.city || undefined,
        fileState: file.property.state || undefined,
        fileZip: file.property.zip || undefined,
      },
    },
    headers(accessToken)
  );
  return data.id;
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const updateEnvelopeProperty = async (envelopeId: string, file: IFile): Promise<string> => {
  const accessToken = await getAccessToken();
  const fileStreetAddress = buildStreetAddress(file.property);

  const { data } = await axios.put(
    `${API_URL}/envelopes/${envelopeId}/metadata?api-version=2.0`,
    {
      primeUserId: file.agent.id,
      listingGuid: file.objectType === 'listing' ? file.id : null,
      saleGuid: file.objectType === 'listing' ? null : file.id,
      fileStreetAddress: fileStreetAddress || undefined,
      fileCity: file.property.city || undefined,
      fileState: file.property.state || undefined,
      fileZip: file.property.zip || undefined,
    },
    headers(accessToken)
  );
  return data;
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const createEmptyEnvelopeWithNoProperty = async (): Promise<string> => {
  const accessToken = await getAccessToken();
  const d = new Date();
  d.setFullYear(d.getFullYear() + 1);
  const expiration = d.toISOString().split('T')[0];
  const { data } = await axios.post(
    `${API_URL}/envelopes?api-version=2.0`,
    {
      expiration,
    },
    headers(accessToken)
  );
  return data.id;
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const updateEnvelopeEmail = async (envelopeId: string, subject: string, body?: string) => {
  const accessToken = await getAccessToken();
  const data: { subject: string; body?: string } = { subject };
  if (typeof body === 'string') {
    data.body = body;
  }
  await axios.patch(`${API_URL}/envelopes/${envelopeId}/emailConfig`, data, headers(accessToken));
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const uploadPrimeDocumentToEnvelope = async (
  envelopeId: string,
  documentId: string,
  fileName: string,
  url: string
) => {
  const accessToken = await getAccessToken();
  const { data, status } = await axios.post(
    `${API_URL}/envelopes/${envelopeId}/documents?api-version=2.0`,
    { fileName, url },
    {
      onUploadProgress: (e) => {
        updateProgressPercentageForDocument(e, documentId);
      },
      ...headers(accessToken),
    }
  );
  return { result: data, status };
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const getEnvelopeDocumentsDTM = async (envelopeId: string) => {
  const accessToken = await getAccessToken();
  const { data } = await axios.get(`${API_URL}/envelopes/${envelopeId}`, headers(accessToken));
  return data.result.documents;
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const getEnvelopeMetadata = async (envelopeId: string): Promise<IEnvelopeMetadata> => {
  const { data } = await graphQlRequest(
    `{
      envelope(id: "${envelopeId}") {
        recipientColorCounter
        metadata {
          primeUserId,
          listingGuid,
          saleGuid,
        },
        documents {
          fileName,
          id,
          numberOfPages,
          uploadStatus
        }
      },
    }`
  );
  const { metadata, recipientColorCounter } = data.data.envelope;

  // @ts-expect-error -- This code was written prior to enforcing lint rules
  const documents = data.data.envelope.documents.map((document) => {
    return { ...document, documentId: document.id, uploadStatus: document.uploadStatus };
  });
  return { ...metadata, documents, fileType: getFileType(metadata), recipientColorCounter };
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const setDocumentBlocks = async (envelopeId: string, documentId: string, blocks: IBlock[]) => {
  const accessToken = await getAccessToken();
  const { status } = await axios.put(
    `${API_URL}/envelopes/${envelopeId}/documents/${documentId}/blocks?api-version=2.0`,
    blocks,
    headers(accessToken)
  );
  return status;
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const getAutoBlocks = async (envelopeId: string): Promise<AutoBlocksRes> => {
  const accessToken = await getAccessToken();
  const { data } = await axios.get(
    `${API_URL}/envelopes/${envelopeId}/auto-blocks?api-version=2.0`,
    headers(accessToken)
  );
  return data.result;
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const addEnvelopeBlocks = async (envelopeId: string, blocks: IBlock[]) => {
  const accessToken = await getAccessToken();
  const { data } = await axios.post(
    `${API_URL}/envelopes/${envelopeId}/blocks?api-version=2.0`,
    { blocks },
    headers(accessToken)
  );
  return data;
};

export async function getEnvelopeAutoReminders(args: { envelopeId: string }): Promise<boolean | null> {
  const { envelopeId } = args;
  const accessToken = await getAccessToken();

  try {
    const res = await axios.get<{
      automatedRemindersEnabled: boolean;
    }>(`${API_URL}/envelopes/${envelopeId}/auto-reminders?api-version=2.0`, headers(accessToken));
    return res.data.automatedRemindersEnabled;
  } catch {
    return null;
  }
}

export async function updateEnvelopeAutoReminders(args: { envelopeId: string; automatedRemindersEnabled: boolean }) {
  const { envelopeId, automatedRemindersEnabled } = args;
  const accessToken = await getAccessToken();

  const res = await axios.patch<unknown>(
    `${API_URL}/envelopes/${envelopeId}/auto-reminders?api-version=2.0`,
    { automatedRemindersEnabled },
    headers(accessToken)
  );
  return res.data;
}

export interface IInPersonSessionResponse {
  signingUrl: string;
  accessToken: string;
  expiresAt: string;
}

/**
 * Hands an envelope to a signer for an in-person session and returns a
 * short-lived signing link. The caller must have access to the envelope; the
 * service decides whether it is actually that signer's turn.
 */
export async function startInPersonSession(envelopeId: string, signerId: string): Promise<IInPersonSessionResponse> {
  const accessToken = await getAccessToken();

  const res = await axios.post<IInPersonSessionResponse>(
    `${API_URL}/envelopes/${envelopeId}/signers/${signerId}/inPersonSession?api-version=2.0`,
    {},
    headers(accessToken)
  );
  return res.data;
}
